import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Utility } from 'helpers/UtilityHelpers';
import { Code } from 'constants/codes';
import apiLogin from "services/loginInterceptors";
import { Location } from 'models/LocationModel';
import { Schedule } from 'models/ScheduleModel';
import { Files } from 'helpers/FileHelpers';
import { Hooks } from 'hooks/commonHooks';

import '../../assets/styles/Appointment.css';
import {type AppointmentOnlineData, type AppointmentFormErrors, INITIAL_APPOINTMENT, validateForm} from 'models/AppointmentOnlineData';
import { Appointment } from 'models/AppointmentModel';
import ReCAPTCHA from 'react-google-recaptcha';


interface ClinicHeaderProps { name: string; address: string; themeColor: string; }
const ClinicHeader: React.FC<ClinicHeaderProps> = ({ name, address, themeColor }) => (
  <div className="clinic-header" style={{ backgroundColor: themeColor }}>
    <div className="clinic-logo">
      <div className="clinic-name">{name}</div>
    </div>
    <div className="clinic-tagline">{address}</div>
  </div>
);

interface SuccessPanelProps { firstName: string; }
const SuccessPanel: React.FC<SuccessPanelProps> = ({ firstName }) => (
  <div className="success-panel">
    <div className="success-icon">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
        stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h3>Appointment Request Submitted!</h3>
    <p>
      Thank you {firstName}<br/> Your appointment request has been received. You may receive a confirmation call soon.
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AppointmentOnline: React.FC = () => {
  const { keyId: paramKeyId } = useParams<{ keyId: string }>();
  const [keyId, setKeyId]     = useState<string>(paramKeyId ?? '');
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<Number>(2);
  const [companyPath, setCompanyPath] = useState<string>('');
  const [locationId, setLocationId] = useState<Number>(2);
  const [clinicName, setClinicName] = useState<string>('');
  const [clinicAddress, setClinicAddress] = useState<string>('');
  const [locationPath, setLocationPath] = useState<string>(keyId);
  
  const [segmentOptions, setSegmentOptions] = Hooks.Form.Options();
  const [locationOptions, setLocationOptions] = Hooks.Form.Options();

  const [form, setForm] = useState<AppointmentOnlineData>(INITIAL_APPOINTMENT);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<AppointmentFormErrors>({});

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const [selectedThemeColor, setSelectedThemeColor] = useState<string>('#0d3b5e');
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<number | null>(null);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState<string>('');
  const [selectedScheduleStartTime, setSelectedScheduleStartTime] = useState<string>('');
  const [selectedScheduleEndTime, setSelectedScheduleEndTime] = useState<string>('');
  const [selectedAppointmentStartTime, setSelectedAppointmentStartTime] = useState<string>('');
  const [selectedAppointmentEndTime, setSelectedAppointmentEndTime] = useState<string>('');

  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [availableDales, setAvailableDates] = useState<Schedule[]>([]);
  const [bookedSlots, setBookedSlots] = useState<Appointment[]>([]);
  const [availableSlots, setAvailableSlots] = useState<Appointment[]>([]);
  
  const [slotLength, setSlotLength] = useState<number>(30);
  const [showTermsOfUse, setShowTermsOfUse] = useState<boolean>(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState<boolean>(false);

  const yearOptions = Array.from({ length: 2026 - 1920 + 1 }, (_, i) => (2026 - i).toString());
  const monthOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const dayOptions = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

  const [dobYear, setDobYear] = useState<string>('');
  const [dobMonth, setDobMonth] = useState<string>('');
  const [dobDay, setDobDay] = useState<string>('');

  const bookableDates = new Set(availableDales.map(item => {
    const date = item.schedule_start_date;
    const dateStr = typeof date === 'string' ? date : date?.toISOString() || '';
    return dateStr.split('T')[0];
  }));

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      appointment_dob: dobYear && dobMonth && dobDay
        ? `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`
        : ''
    }));
  }, [dobYear, dobMonth, dobDay]);
 
  const update = <K extends keyof AppointmentOnlineData>(key: K, value: AppointmentOnlineData[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: undefined }));
    }
  };
  
  const getMonthLabel = (date: Date) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const buildCalendar = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const dayOffset = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const cells = [] as Array<{label: string; date: string | null; isToday: boolean}>;

    for (let i = 0; i < dayOffset; i += 1) {
      cells.push({ label: '', date: null, isToday: false });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const current = new Date(year, month, day);
      const isoDate = formatDate(current);
      const today = formatDate(new Date()) === isoDate;
      cells.push({ label: String(day), date: isoDate, isToday: today });
    }

    while (cells.length < 42) {
      cells.push({ label: '', date: null, isToday: false });
    }

    return cells;
  };

  const calendarCells = buildCalendar(calendarMonth);

  const handleSegmentSelect = (value: number) => {
    setSelectedSegment(value);
    setSelectedLocation(null);
    setSelectedScheduleDate('');
    setSelectedTimeSlot('');
  };

  const handleLocationSelect = (value: number) => {
    setSelectedLocation(value);
    setLocationId(value);
    update('location_id', value as AppointmentOnlineData['location_id']);
    setSelectedScheduleDate('');
    setSelectedTimeSlot('');
  };

  const selectedSegmentLabel = segmentOptions.find(option => option.value === selectedSegment)?.label || '';
  const selectedLocationLabel = locationOptions.find(option => option.value === selectedLocation)?.label || '';

  const steps = [
    `${selectedSegmentLabel ? selectedSegmentLabel : 'Select Exam'}`,
    `${selectedLocationLabel ? selectedLocationLabel : 'Select Location'}`,
    `${selectedScheduleDate ? selectedScheduleDate : 'Select date'}`,
    `${selectedTimeSlot ? selectedTimeSlot.replace("-","")  : 'Select time'}`,
    'Patient details',
    'Finalize',
  ];

  const currentStep = submitted
    ? 6
    : selectedTimeSlot
      ? 5
      : selectedScheduleDate
        ? 4
        : selectedLocation !== null
          ? 3
          : selectedSegment !== null
            ? 2
            : 1;

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex >= currentStep) return;

    switch (stepIndex) {
      case 1:
        setSelectedSegment(null);
        setSelectedLocation(null);
        setSelectedScheduleDate('');
        setSelectedTimeSlot('');
        break;
      case 2:
        setSelectedLocation(null);
        setSelectedScheduleDate('');
        setSelectedTimeSlot('');
        break;
      case 3:
        setSelectedScheduleDate('');
        setSelectedTimeSlot('');
        break;
      case 4:
        setSelectedTimeSlot('');
        break;
      default:
        break;
    }
  };

  const handleDateSelection = (date: string | null) => {
    if (!date) return;
    setSelectedScheduleDate(date);

    const selectedSlot = availableDales.find(item => {
      const scheduleDate = typeof item.schedule_start_date === 'string'
        ? item.schedule_start_date
        : item.schedule_start_date instanceof Date
          ? item.schedule_start_date.toISOString()
          : '';
       return scheduleDate.split('T')[0] === date;
    });

    if (selectedSlot) {
      const startTime = selectedSlot.schedule_start_time || '';
      const endTime = selectedSlot.schedule_end_time || '';
      const doctorId = selectedSlot.doctor_id || null;
      setSelectedScheduleStartTime(startTime);
      setSelectedScheduleEndTime(endTime);
      setSelectedDoctor(doctorId);
      //console.log('Selected date:', date, 'Start time:', startTime, 'End time:', endTime);
    } else {
      setSelectedScheduleStartTime('');
      setSelectedScheduleEndTime('');
      setSelectedDoctor(null);
    }
  };

    const handleTimeSelection = (slot: Appointment) => {
    setSelectedTimeSlot(`${formatTime(slot.appointment_start_time)} - ${formatTime(slot.appointment_end_time)}`);
    setSelectedAppointmentStartTime(slot.appointment_start_time || '');
    setSelectedAppointmentEndTime(slot.appointment_end_time || '');
  };

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const [validationErrors] = validateForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setToast('Please fill in all required fields before submitting.');
      setTimeout(() => setToast(null), 4000);
      return;
    }

    // Anti-spam checks
    if (!recaptchaToken) {
      setToast('Please complete the reCAPTCHA verification.');
      setTimeout(() => setToast(null), 4000);
      return;
    }

    if (form.website.trim()) {
      // Honeypot field filled, likely spam
      console.warn('Honeypot field filled, potential spam detected');
      setToast('There was a problem submitting your appointment. Please try again.');
      setTimeout(() => setToast(null), 4000);
      return;
    }

    try {

      let commentData = 'Online booking\n';
      commentData += form.appointment_first_name ? `Name: ${form.appointment_first_name} ${form.appointment_last_name}\n` : '';
      commentData += form.appointment_phone ? `Phone: ${form.appointment_phone}\n` : '';
      commentData += form.appointment_email ? `Email: ${form.appointment_email}\n` : '';
      commentData += form.appointment_health_card ? `Health Card: ${form.appointment_health_card}\n` : '';
      commentData += form.appointment_dob ? `Date of Birth: ${form.appointment_dob}\n` : '';

      //console.log('Submitting appointment with data:', commentData);

      const response = await fetch(`${Code.Path.SERVER}appointment/online`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          //...form,
          company_id: companyId,
          location_id: locationId,
          branch_id: locationId,
          client_id: 4787, // online booking client ID
          status_id: 5, // New entry status
          doctor_id: selectedDoctor,
          appointment_date: Utility.Date.Current(),
          appointment_start_date: selectedScheduleDate,
          appointment_end_date: selectedScheduleDate,
          appointment_start_time: selectedAppointmentStartTime,
          appointment_end_time: selectedAppointmentEndTime,
          appointment_comment: commentData,
          recaptcha_token: recaptchaToken,
          keyId,
        }),
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Network error during submission:', err);
      setToast('There was a problem submitting your appointment. Please try again.');
      setTimeout(() => setToast(null), 4000);
    }
  };

 useEffect(() => {
  const fetchLocation = async () => {
    document.body.style.backgroundImage = "none";
    if (locationPath) {
      try {
        const url = `${Code.Path.SERVER}auth/location/${locationPath}`;
        const response = await apiLogin.get(url);
        console.clear();
        //console.log("response>>>>>", response);
        if (response) {
          const result = response.data;
          //console.log("result>>>>>", result.data);
          if (result.status === "success" && result.code === 200) {
            const location = result.data as Location;
            if (location) {
              setCompanyId(location.company_id || 0);
              setCompanyPath(location.company_path || '');
              setLocationId(location.location_id || 0);
              setClinicName(location.company_legal_name || '');
              setClinicAddress(location.location_title?.replace(/\r?\n/g, "") || '');
              setSelectedThemeColor(location.theme_color || '#0d3b5e');
            }
          }
        }
      } catch (error) {
        console.error("fetchLocation error >>>>>", error);
        setCompanyId(11);
        setLocationId(13);
        setClinicName(Utility.Convert.TitleCase(locationPath?.replaceAll("-"," ") || ''));
        setClinicAddress('');

        
      }
    }
  };

  fetchLocation();
}, [locationPath]);

useEffect(() => {
  if(companyPath){
    Files.Json.Http("segment", Code.Option.Common.NOTHING, setSegmentOptions, setError, companyPath);
  }
}, [companyPath]);

//bypassing auto selection of single segment
// useEffect(() => {
//   if(segmentOptions && segmentOptions.length == 1){
//     setSelectedSegment(segmentOptions[0].value)
//   }
// }, [segmentOptions]);

//bypassing auto selection of single location
// useEffect(() => {
//   if(locationOptions && locationOptions.length == 1){
//     setSelectedLocation(locationOptions[0].value)
//   }
// }, [locationOptions]);

useEffect(() => {
  if(selectedSegment && selectedSegment>9){
    Files.Json.Http(`location${selectedSegment}`, Code.Option.Common.NOTHING, setLocationOptions, setError, companyPath);
  }
}, [selectedSegment]);

useEffect(() => {
  if(selectedLocation && selectedLocation>9 && selectedSegment && selectedSegment>9){
    const fetchScheduleData = async () => {
      const year = calendarMonth.getFullYear();
      const month = calendarMonth.getMonth() + 1;
      try {
        const response = await fetch(`${Code.Path.SERVER}custom/schedule/${companyId}/${selectedLocation}/${selectedSegment}/${year}/${month}`);
        if (response.ok) {
          const data = await response.json();
          //console.log('Schedule data fetched:', data);
          if (data && data.status === 'success' && data.code === 200) {
            setAvailableDates(data.data || []);
          } else {
            console.error('API returned non-success status:', data);
          }
        } else {
          console.error('Failed to fetch schedule data:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching schedule data:', error);
      }
    };

    fetchScheduleData();
  }
}, [selectedLocation, calendarMonth]);

useEffect(() => {
  if(selectedLocation && selectedDoctor && selectedScheduleDate){
    const fetchAppoitmentData = async () => {
      const date = new Date(selectedScheduleDate).toISOString().split('T')[0];
      try {
        //console.log("selectedScheduleDate>>>>>", selectedScheduleDate);
        const response = await fetch(`${Code.Path.SERVER}custom/appointment/${companyId}/${selectedLocation}/${selectedDoctor}/${date}`);
        if (response.ok) {
          const data = await response.json();
          //console.log('Appointment data fetched:', data);
          if (data && data.status === 'success' && data.code === 200) {
            setBookedSlots(data.data || []);
          } else {
            console.error('API returned non-success status:', data);
          }
        } else {
          console.error('Failed to fetch appointment data:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching appointment data:', error);
      }
    };

    fetchAppoitmentData();
  }
}, [selectedScheduleDate]);

useEffect(() => {
  if (bookedSlots.length > 0 && selectedScheduleDate && selectedScheduleStartTime && selectedScheduleEndTime) {
    //console.log('all Booked appointments for selected date:', bookedSlots);
    //const selectedStart = new Date(`${selectedScheduleDate}T${selectedScheduleStartTime}`);
    //const selectedEnd = new Date(`${selectedScheduleDate}T${selectedScheduleEndTime}`);

   const availableSlots = getAvailableTimeSlots(
      selectedScheduleDate,
      selectedScheduleStartTime,
      selectedScheduleEndTime,
      bookedSlots
    );
  
    setAvailableSlots(availableSlots);
  }
}, [bookedSlots]);
 
/**
 * Converts time string (HH:MM:SS or HH:MM) to minutes since midnight
 */
function timeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
}
 
/**
 * Converts minutes since midnight to time string (HH:MM:SS)
 */
function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;
}

const formatTime = (time: string | undefined): string => {
  if (!time) return '-';
  
  const date = new Date(`2000-01-01 ${time}`);
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};
 

function getAvailableTimeSlots(
  selectedScheduleDate: string | Date,
  selectedScheduleStartTime: string,
  selectedScheduleEndTime: string,
  alreadyBooked: Appointment[] = []
): Appointment[] {
  // Convert date to comparable format
  const dateStr =
    selectedScheduleDate instanceof Date
      ? selectedScheduleDate.toISOString().split("T")[0]
      : selectedScheduleDate;
 
  // Convert times to minutes for easier calculation
  const officeStartMinutes = timeToMinutes(selectedScheduleStartTime);
  const officeEndMinutes = timeToMinutes(selectedScheduleEndTime);
 
  // Generate all 30-minute slots for office hours
  const allSlots: Appointment[] = [];
  for (let minutes = officeStartMinutes; minutes < officeEndMinutes; minutes += slotLength) {
    const startTime = minutesToTime(minutes);
    const endTime = minutesToTime(minutes + slotLength);
    allSlots.push({ appointment_start_time: startTime, appointment_end_time: endTime });
  }
 
  // Extract booked slots for this specific date
  const bookedOnThisDate = alreadyBooked.filter((appointment) => {
    const appointmentDate =
      appointment.appointment_start_date instanceof Date
        ? appointment.appointment_start_date.toISOString().split("T")[0]
        : String(appointment.appointment_start_date).split("T")[0];
    return appointmentDate === dateStr;
  });
 
  // Convert booked slots to minutes for comparison
  const bookedSlots = bookedOnThisDate.map((appointment) => ({
    startMinutes: timeToMinutes(appointment.appointment_start_time || "00:00"),
    endMinutes: timeToMinutes(appointment.appointment_end_time || "00:00"),
  }));
 
  // Filter out booked slots
  const availableSlots = allSlots.filter((slot) => {
    const slotStartMinutes = timeToMinutes(slot.appointment_start_time  || "00:00");
    const slotEndMinutes = timeToMinutes(slot.appointment_end_time || "00:00");
 
    // Check if this slot overlaps with any booked slot
    const isBooked = bookedSlots.some(
      (booked) =>
        slotStartMinutes < booked.endMinutes && slotEndMinutes > booked.startMinutes
    );
 
    return !isBooked;
  });
 
  return availableSlots;
}

useEffect(() => {
  if (paramKeyId) setKeyId(paramKeyId);
}, [paramKeyId]);


return (
    <div className="page-wrapper">
      <ClinicHeader name={clinicName} address={clinicAddress} themeColor={selectedThemeColor} />

      <div className="form-card">
        <div className="form-title-bar">
          <h2>Appointment Booking</h2>
          <span className="badge">Confidential</span>
        </div>

        <div className="stepper">
          {steps.map((label, index) => {
            const stepIndex = index + 1;
            const isCompleted = stepIndex < currentStep;
            const isActive = stepIndex === currentStep;
            return (
              <div
                className={['step-item', isCompleted ? 'completed' : '', isActive ? 'active' : '', stepIndex < currentStep ? 'clickable' : ''].join(' ').trim()}
                key={label}
                title={label}
                onClick={() => handleStepClick(stepIndex)}
              >
                <div className="step-marker">
                  {isCompleted ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : stepIndex}
                </div>
                <div className="step-label">{label}</div>
                {index < steps.length - 1 && <div className={['step-connector', isCompleted ? 'completed' : ''].join(' ').trim()} />}
              </div>
            );
          })}
        </div>

        <div className="form-body">
          {submitted ? (
            <SuccessPanel firstName={form.appointment_first_name} />
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              
              {/* ══ 1. Appointment type ══════════════════════════════════════ */}
              {selectedSegment === null && (
                <>
                  <div className="section">
                    <div className="section-label">Select an appointment type</div>
                  </div>
                  <div className="field-button-frame">
                    {segmentOptions.length > 0 ? (
                      segmentOptions.map((option) => (
                        <button
                          type="button"
                          className={['field-btn', selectedSegment === option.value ? 'selected' : ''].join(' ').trim()}
                          key={option.value}
                          onClick={() => handleSegmentSelect(option.value as number)}
                        >
                          {option.label}
                        </button>
                      ))
                    ) : (
                      <div className="field-note">Loading appointment types...</div>
                    )}
                  </div>
                </>
              )}

              {/* ══ 2. Location ══════════════════════════════════════ */}
              {selectedSegment !== null && selectedLocation === null && (
                <>
                  <div className="section">
                    <div className="section-label">Select a location</div>
                  </div>
                  <div className="field-button-frame">
                    {locationOptions.length > 0 ? (
                      locationOptions.map((option) => (
                        <button
                          type="button"
                          className={['field-btn', selectedLocation === option.value ? 'selected' : ''].join(' ').trim()}
                          key={option.value}
                          onClick={() => handleLocationSelect(option.value as number)}
                        >
                          {option.label}
                        </button>
                      ))
                    ) : (
                      <div className="field-note">Loading available locations...</div>
                    )}
                  </div>
                </>
              )}

              {/* ══ 3. Calendar ══════════════════════════════════════ */}
              {selectedLocation !== null && selectedScheduleDate === '' && (
                <>
                  <div className="section">
                    <div className="section-label">Choose an appointment date</div>
                  </div>
                  <div className="calendar-panel">
                    <div className="calendar-header">
                      <button
                        type="button"
                        className="calendar-nav"
                        onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                      >
                        ‹
                      </button>
                      <div className="calendar-title">{getMonthLabel(calendarMonth)}</div>
                      <button
                        type="button"
                        className="calendar-nav"
                        onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                      >
                        ›
                      </button>
                    </div>
                    <div className="calendar-weekdays">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((weekday) => (
                        <div key={weekday} className="calendar-weekday">{weekday}</div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {calendarCells.map((cell, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={['calendar-day', !cell.date ? 'calendar-day--empty' : '', selectedScheduleDate === cell.date ? 'selected' : '', cell.isToday ? 'today' : '', cell.date && bookableDates.has(cell.date) ? 'available' : '', cell.date && cell.date < formatDate(new Date()) ? 'field-past ' : ''].join(' ').trim()}
                          disabled={!cell.date || !bookableDates.has(cell.date) || (cell.date ? cell.date < formatDate(new Date()) : false)}
                          onClick={() => handleDateSelection(cell.date)}
                        >
                          {cell.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {selectedScheduleDate && (
                    <div className="selection-summary">Selected date: {selectedScheduleDate}</div>
                  )}
                </>
              )}

              {/* ══ 4. Time Slots available here ══════════════════════════════════════ */}
              {selectedScheduleDate !== '' && selectedTimeSlot === '' && (
                <>
                  <div className="section">
                    <div className="section-label">Choose a time slot</div>
                  </div>
                  <div className="field-button-frame">
                    {availableSlots.map((slot) => (
                      <button
                        key={`${slot.appointment_start_time} - ${slot.appointment_end_time}`}
                        type="button"
                        className={['field-btn'].join(' ').trim()}
                        onClick={() => handleTimeSelection(slot)}
                      >
                        {formatTime(slot.appointment_start_time)} - {formatTime(slot.appointment_end_time)}
                      </button>
                    ))}
                  </div>
                  {availableSlots.length === 0 && (
                    <div className="field-note pb8">No available time slots for the selected date.</div>
                  )}
                  <div className="change-date" onClick={() => handleStepClick(3)}>Choose Different Date</div>
                  {selectedTimeSlot && (
                    <div className="selection-summary">Selected time slot: {selectedTimeSlot}</div>
                  )}
                </>
              )}

              {/* ══ 5. Patient details form ══════════════════════════════════════ */}
              {selectedTimeSlot && (
                <>
                  <div className="section">
                    <div className="section-label">Appointment Summary</div>
                  </div>
                 
                  <div className="grid-2 mb4">
                    <div className="selection-summary">
                      {selectedSegmentLabel && (<> Exam Type: <span>{selectedSegmentLabel}</span><br /> </> )}
                      {selectedLocationLabel && (<> Location: <span>{selectedLocationLabel}</span><br /> </> )}
                    </div>
                    <div className="selection-summary">
                      {selectedScheduleDate && (<> Date: <span>{selectedScheduleDate}</span><br /> </> )}
                      {selectedTimeSlot && (<> Time: <span>{selectedTimeSlot}</span><br /> </> )}
                    </div>
                  </div>
                    
                    
                  
                  <div className="section">
                    <div className="section-label">Patient details</div>
                  </div>
                  <div className="field-grid">
                    <label className="field-block">
                      <span>First name <span className="clinic-req">*</span></span>
                      <input
                        type="text"
                        value={form.appointment_first_name}
                        onChange={(e) => update('appointment_first_name', e.target.value)}
                        className={errors.appointment_first_name ? 'error-border' : ''}
                      />
                    </label>
                    <label className="field-block">
                      <span>Last name</span>
                      <input
                        type="text"
                        value={form.appointment_last_name}
                        onChange={(e) => update('appointment_last_name', e.target.value)}
                        className={errors.appointment_last_name ? 'error-border' : ''}
                      />
                    </label>
                    <div className="field">
                    <label>Date of Birth <span className="clinic-req">*</span></label>
                      <div className={`dob-selects ${errors.appointment_dob ? 'error' : ''}`}>
                        <div className="grid-3">
                          <div className="field">
                            <select value={dobYear} onChange={e => setDobYear(e.target.value)}>
                              <option value="">Year</option>
                              {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
                            </select>
                          </div>
                          <div className="field">
                            <select value={dobMonth} onChange={e => setDobMonth(e.target.value)}>
                              <option value="">Month</option>
                              {monthOptions.map(month => <option key={month} value={month}>{month}</option>)}
                            </select>
                          </div>
                          <div className="field">
                            <select value={dobDay} onChange={e => setDobDay(e.target.value)}>
                              <option value="">Day</option>
                              {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                    <label className="field-block">
                      <span>Health card number</span>
                      <input
                        type="text"
                        value={form.appointment_health_card}
                        onChange={(e) => update('appointment_health_card', e.target.value)}
                      />
                    </label>
                    <label className="field-block">
                      <span>Phone <span className="clinic-req">*</span></span>
                      <input
                        type="tel"
                        value={form.appointment_phone}
                        onChange={(e) => update('appointment_phone', e.target.value)}
                        className={errors.appointment_phone ? 'error-border' : ''}
                      />
                    </label>
                    <label className="field-block">
                      <span>Email</span>
                      <input
                        type="email"
                        value={form.appointment_email}
                        onChange={(e) => update('appointment_email', e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="selection-summary">&nbsp;</div>

                  <div
                    className={['terms-block', form.terms ? 'checked' : '', errors.terms ? 'error-border' : ''].join(' ')}
                  >
                    <div className="custom-check" onClick={() => update('terms', !form.terms)}>
                      {form.terms && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                          stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <div className="terms-text">
                      <div>
                        I hereby consent to the collection, use, and disclosure of my eye care information 
                        by {clinicName} for the purpose of booking and managing my appointment, in accordance with 
                        the <a onClick={() => setShowTermsOfUse(showTermsOfUse ? false : true)}>Terms of Use</a> and <a onClick={() => setShowPrivacyPolicy(showPrivacyPolicy ? false : true)}>Privacy Policy</a>. 
                        This includes administrative and support services such as scheduling eye care appointments, managing 
                        patient records, issuing reminders, telephone communications, and processing applicable billing or provincial 
                        health insurance claims. I confirm that the information provided is accurate to the best of my knowledge.
                        <span className="clinic-req">*</span>
                      </div>
                      {showTermsOfUse && (
                        <div className='terms-border'>
                          <h3>TERMS OF USE</h3>
                          <p>These Terms of Use (“Terms”) describe the rules and conditions for accessing and using the Service Portal. The version posted on the portal at the time of your use applies. By continuing to use the Service Portal after updates are made, you accept those changes. If you do not agree with these Terms or any updates, you must stop using the Service Portal immediately.</p>
                        
                          <strong>1. Definitions</strong>
                          <p>•	“Clinic” refers to the healthcare provider offering access to the Service Portal and related services.</p>
                          <p>•	“Service Portal” refers to the online booking and patient management system available through nexvizon.com.</p>
                          <p>•	“User” or “You” refers to any individual who accesses or uses the Service Portal.</p>
                        
                          <strong>2. Access and Information Security</strong>
                          <p>Certain features of the Service Portal may require you to create an account and provide personal details. By doing so, you agree that all information you submit is accurate, complete, and current.</p>
                          <p>You are responsible for all activities carried out. You must notify the Clinic promptly if you suspect any unauthorized access or misuse of your account.</p>
                        
                          <strong>3. Use of the Service Portal and Consent</strong>
                          <p>By using the Service Portal—including booking appointments or submitting personal information—you consent to the collection, use, and sharing of your information by the Clinic and its authorized partners. This is done for purposes such as operating the portal and supporting your care.</p>
                          <p>If you use the Service Portal for another individual (for example, a dependent or family member), you confirm that:</p>
                          <p>•	You have their permission to act on their behalf</p>
                          <p>•	You have their consent to share their personal information</p>
                          <p>•	You accept these Terms on their behalf</p>
                        
                          <strong>4. Communication Consent</strong>
                          <p>Email Communication</p>
                          <p>By providing your email address, you agree to receive messages related to your use of the Service Portal. These communications are necessary to maintain your account. If you withdraw your consent to receive emails, your access to the Service Portal may be restricted or terminated.</p>
                          <p>You may update your contact details and preferences through the Service Portal or by using links included in email communications.</p>
                          <p>SMS/Text Messages</p>
                          <p>You may receive text messages such as welcome message, appointment reminders, promotional offers, pick up notification etc. Message frequency will depend on your care needs. You can opt out at any time by replying “STOP,” or request assistance by replying “HELP.” Standard carrier charges may apply.</p>

                          <strong>5. Personal Information and Privacy</strong>
                          <p>The Clinic collects personal information to:</p>
                          <p>•	Schedule appointments</p>
                          <p>•	Confirm your identity and health information</p>
                          <p>•	Provide access to portal features</p>
                          <p>All personal information is handled in accordance with applicable privacy laws and the Clinic’s privacy policy.</p>

                          <strong>6. Disclaimer</strong>
                          <p>The Service Portal is provided on an “as is” and “as available” basis. The Clinic and its service providers do not guarantee that the portal will be uninterrupted, error-free, or secure, and they make no warranties of any kind, whether express or implied.</p>
                          <p>Except as expressly stated in this Agreement, all warranties, representations, and conditions are excluded. This includes, without limitation, any implied warranties of merchantable quality, fitness for a particular purpose or those arising by statute, law, trade usage, or course of dealing.</p>
                          <p>NextVizon does not guarantee uninterrupted or error-free operation of the Software. While NextVizon provides support and maintenance in accordance with this Agreement, it makes no warranties regarding the continuous availability, flawless operation, or absolute security of the Software.</p>
                          <p>Third-party hardware, software, and communications systems: NextVizon makes no representation or warranty regarding the quality, performance, compatibility, or suitability of any third-party hardware, software, or communication systems used with the Software. Failures due to such third-party systems — including viruses, line failures, delays, transmission errors, or data loss — are expressly excluded from NextVizon’s liability.</p>
                          <p>Limit of liability: In no event shall NextVizon be liable for indirect, incidental, consequential, special, or punitive damages, including (but not limited to) lost profits, business interruption, loss of data, loss of goodwill, taxation, insurance issues arising from the use or inability to use the Software.</p>
                          <p>No additional warranties: No oral or written information, advice, or consultation provided by NextVizon, its employees, agents, dealers, or contractors shall create any warranty or expand the scope of warranties expressly stated in this Agreement.</p>
                        
                          <strong>7. Limitation of Liability</strong>
                          <p>To the fullest extent permitted by law, the total liability of the Clinic and its service providers is limited to the lesser of:</p>
                          <p>•	The amount paid (if any) for access to the Service Portal, or</p>
                          <p>•	Fifty dollars ($50)</p>
                        
                          <strong>8. Governing Law</strong>
                          <p>These Terms are governed by the laws of the province in which the Clinic operates. Any disputes will be resolved in the courts of that province.</p>
                        
                          <strong>9. Suspension or Termination</strong>
                          <p>The Clinic may suspend or terminate your access to the Service Portal if it believes you have violated these Terms or as otherwise permitted under these Terms.</p>
                        
                          <p>By using the Service Portal, you acknowledge that you have read, understood, and agreed to these Terms of Use.</p>

                        </div>
                      )}
                      {showPrivacyPolicy && (
                        <div className='terms-border'>

                          <h3>PRIVACY POLICY</h3>
                         
                          <p>Please read this Privacy Policy carefully before using the Next Vizon Optometry online booking feature.</p>
                         
                          <strong>Overview</strong>
                          <p>Infotechnext Systems Inc. (“we”, “our”, or “us”) provides the Next Vizon online booking platform to optometry clinics or Optometrists (“Healthcare Providers”). By using this service and providing your personal information, you consent to its collection, use, and processing as described below and in your Healthcare Provider’s own privacy policy.</p>
                         
                          <strong>Role of the Healthcare Provider</strong>
                          <p>Your Healthcare Provider (clinic or optometrist) is responsible for your personal and health information. They act as the custodian of your information and determine how it is used and protected.</p>
                          <p>Infotechnext Systems Inc. acts only as a service provider, processing information on behalf of your Healthcare Provider to enable appointment booking and related services. We are not a party to the direct healthcare relationship between you and your provider.</p>
                         
                          <strong>Information We Collect</strong>
                          <p>When you use the online booking system, we may collect:</p>
                          <p>•	Basic personal details (e.g., name, phone number, email)</p>
                          <p>•	Appointment information (e.g., date, time, visit reason)</p>
                          <p>•	Optional information you provide related to your visit</p>
                          <p>•	Insurance Information (optional)</p>
                          <p>This information may form part of your clinic’s records.</p>

                          <strong>How Your Information Is Used</strong>
                          <p>Your information is processed only as necessary to:</p>
                          <p>•	Book, confirm, reschedule, or cancel appointments</p>
                          <p>•	Communicate with you regarding your appointments</p>
                          <p>•	Support your Healthcare Provider’s operations</p>
                          <p>•	Text or Email you as needed</p>
                          <p>We process your information strictly according to your Healthcare Provider’s instructions and applicable privacy laws.</p>

                          <strong>Consent</strong>
                          <p>By using this booking service, you consent to the processing of your personal information for the purposes outlined above.</p>
                          <p>You may withdraw your consent at any time, subject to legal or contractual restrictions, by contacting your Healthcare Provider.</p>

                          <strong>Data Sharing</strong>
                          <p>Your information is shared only:</p>
                          <p>•	With your selected Healthcare Provider</p>
                          <p>•	With authorized service providers supporting system functionality</p>
                          <p>•	Any third party app integrated associated with Next Vizon software</p>
                          <p>We do not sell or rent your personal information.</p>

                          <strong>Data Protection & Privacy Incidents</strong>
                          <p>We implement appropriate technical and organizational safeguards to protect your information.</p>
                          <p>In the event of a data breach or privacy incident, we will notify your Healthcare Provider, who is responsible for determining whether notification to you is required under applicable laws.</p>

                          <strong>Data Retention</strong>
                          <p>Your information is retained only as long as necessary for appointment management and as required by applicable laws and your Healthcare Provider’s policies.</p>
                         
                          <strong>Age Requirements</strong>
                          <p>Users should be at least 16 years old to use the online booking system. If under the age of majority, parental or guardian consent may be required. Parents or guardians may use the system on behalf of minors.</p>

                          <strong>Your Rights</strong>
                          <p>Your rights regarding access, correction, or complaints about your personal information are governed by your Healthcare Provider’s privacy practices. Please contact your provider directly for assistance.</p>
                         
                          <strong>Changes to This Policy</strong>
                          <p>This policy may be updated from time to time. Continued use of the booking system constitutes acceptance of any changes.</p>
                         
                          <strong>Contact</strong>
                          <p>For questions or concerns about your personal information, please contact your Healthcare Provider.</p>
                        </div>
                      )}
                      
                    </div>
                    

                  </div>

                  {/* Anti-spam measures */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={(e) => update('website', e.target.value)}
                    style={{ display: 'none' }}
                    autoComplete="off"
                    tabIndex={-1}
                  />
                  <div className="recaptcha-container" style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <ReCAPTCHA
                      sitekey="6Lc8uG4qAAAAAHCmxZUJINB2c2wH13iuBCWOwr5k" // Replace with your actual Google reCAPTCHA site key
                      onChange={setRecaptchaToken}
                    />
                  </div>

                  <button type="submit" className="submit-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 2L11 13" /><path d="M22 2L15 22 11 13 2 9l20-7z" />
                    </svg>
                    Book Appointment
                  </button>
                </>
              )}
              <div className="privacy-notice">
                🔒 Your information is encrypted and protected under HIPAA / PHIPA.<br />
                This form is for new and returning patients of {clinicName}.
              </div>
              <div className="field-poweredby" style={{opacity : 1}}>
                <span>Powered by:</span> 
                <img src="/assets/images/basic/next-vizon.png" alt="NextVizon Optometry Software" /> 
                <span>Optometry Software</span>
              </div>
                
            </form>
          )}
        </div>
      </div>
      {toast && (
        <div className="toast-error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
};

export default AppointmentOnline;